import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Filter, Search, ArrowUpRight, ArrowDownLeft,
  Clock, CheckCircle, XCircle, DollarSign,
  MessageCircle, Calendar, ChevronDown, ChevronRight,
  FileText, Star, ExternalLink, AlertCircle, X,
  Receipt, ChevronLeft, ChevronRight as ChevronRightIcon, Download,
  User, MapPin, Timer
} from "lucide-react";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { fetchUserOffers, updateOffer } from "../redux/Slices/offerSlice";
import { fetchQuotesByUserIdWithFilters } from "../redux/Slices/quoteSlice";
import { useSelector } from "react-redux";
import { RequestQuoteModal } from "../components/ui";
import { OfferStatusEnum } from "../modules/shared/model/offer.interfaces";
import { PurchaseQuoteTypeEnum } from "../modules/shared/model/quote.enum";
import OfferDetailsModal from "../components/ui/OfferDetailsModal";
import PurchasedQuoteListing from "../components/ui/PurchasedQuoteListing";
import { useNavigate } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import ClientProfile1 from "../modules/client/components/ClientProfile1";
import { calculateEmergencyCost } from "../lib/emergencyCostUtils";
import ProfileCompletionDialog from "../components/ui/ProfileCompletionDialog";
import VerificationRequirementDialog from "../components/VerificationRequirementDialog";
import { useVerification } from "../hooks/useVerification";
import { checkUserBillingInfo } from "../utils/billingUtils";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import BillingAddressRequirementDialog from "../components/ui/BillingAddressRequirementDialog";
import { useTranslation } from "react-i18next";

const OffersDashboard = () => {
  const { t } = useTranslation();
  // Redux hooks
  const dispatch = useAppDispatch();
  const { offers, loading, error, totalCount, currentPage: reduxCurrentPage, totalPages, hasNextPage } = useAppSelector((state: any) => state.offers);
  const { user } = useSelector((state: any) => state.auth);
  const { settings: categoryFormSettings } = useAppSelector((state: any) => state.categoryFormSettings);

  // State management
  const [activeTab, setActiveTab] = useState(() => {
    // Get the saved tab from localStorage, default to "received"
    return localStorage.getItem("myOffersActiveTab") || "received";
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [dateRange, setDateRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOffer, setSelectedOffer] = useState(null);
  const navigate = useNavigate();

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  // Client profile dialog state
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [selectedClientUser, setSelectedClientUser] = useState<any>(null);

  // Profile and verification dialog states
  const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
  const [selectedOfferForBilling, setSelectedOfferForBilling] = useState<any>(null);
  
  // Use verification hook
  const { isVerified, goToVerification, verificationData } = useVerification();
  
  // Track if verification data has been loaded at least once
  const [verificationDataLoaded, setVerificationDataLoaded] = useState(false);

  // Memoized professional for edit modal to avoid re-renders triggering effects
  const professionalForEdit = React.useMemo(() => {
    const user = editingOffer?.job?.userId;
    if (!user || !user.id) return {} as any;
    return {
      id: user.id,
      name: user.firstName || user.name || "Client",
    } as any;
  }, [editingOffer?.job?.userId?.id, editingOffer?.job?.userId?.firstName, editingOffer?.job?.userId?.name]);

  // Pagination state for each tab
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsInitialized, setCountsInitialized] = useState(false);
  
  // Loading state for current tab
  const [isLoadingCurrentTab, setIsLoadingCurrentTab] = useState(false);

  // Stats state - separate counts for each metric
  const [stats, setStats] = useState({
    totalOffers: 0,
    acceptedOffers: 0,
    pendingOffers: 0,
    rejectedOffers: 0
  });

  // Purchased quotes count from quotes Redux slice
  const quotesState = useAppSelector((state: any) => state.quotes);
  const purchasedQuotesCount = quotesState?.totalCount || 0;

  // Single function to fetch counts with proper loading state management
  const fetchAllCounts = async (forceRefresh = false, tabContext = activeTab) => {
    if (!user?.id || (countsLoading && !forceRefresh)) return;
    
    setCountsLoading(true);
    try {
      const isSentParam = tabContext === "received";
      
      // Fetch counts for different statuses in parallel
      // Store the responses but don't let them overwrite the main offers list
      const [totalResponse, acceptedResponse, pendingResponse, rejectedResponse] = await Promise.allSettled([
        // Total offers count for current tab
        dispatch(fetchUserOffers({
          page: 1,
          limit: 1,
          isSent: isSentParam,
          userId: user?.id,
          statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Deleted, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        })),
        // Accepted offers count
        dispatch(fetchUserOffers({
          page: 1,
          limit: 1,
          isSent: isSentParam,
          userId: user?.id,
          status: OfferStatusEnum.Accepted,
          statusIds: [OfferStatusEnum.Accepted].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        })),
        // Pending (Approved) offers count
        dispatch(fetchUserOffers({
          page: 1,
          limit: 1,
          isSent: isSentParam,
          userId: user?.id,
          status: OfferStatusEnum.Approved,
          statusIds: [OfferStatusEnum.Approved].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        })),
        // Rejected offers count
        dispatch(fetchUserOffers({
          page: 1,
          limit: 1,
          isSent: isSentParam,
          userId: user?.id,
          status: OfferStatusEnum.Rejected,
          statusIds: [OfferStatusEnum.Rejected].toString(),
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }))
      ]);

      // Only update stats if we're still on the same tab
      if (tabContext !== activeTab) {
        return; // Tab changed during fetch, discard results
      }

      // Update stats based on successful responses
      const newStats = { ...stats };
      
      if (totalResponse.status === "fulfilled" && totalResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = totalResponse.value.payload as any;
        newStats.totalOffers = Number(payload?.totalCount || 0);
      }
      
      if (acceptedResponse.status === "fulfilled" && acceptedResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = acceptedResponse.value.payload as any;
        newStats.acceptedOffers = Number(payload?.totalCount || 0);
      }
      
      if (pendingResponse.status === "fulfilled" && pendingResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = pendingResponse.value.payload as any;
        newStats.pendingOffers = Number(payload?.totalCount || 0);
      }
      
      if (rejectedResponse.status === "fulfilled" && rejectedResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = rejectedResponse.value.payload as any;
        newStats.rejectedOffers = Number(payload?.totalCount || 0);
      }

      setStats(newStats);
      setCountsInitialized(true);
    } catch (error) {
      console.error("Error fetching counts:", error);
    } finally {
      setCountsLoading(false);
    }
  };

  // Fetch purchased quotes count separately
  const fetchPurchasedQuotesCount = async () => {
    if (!user?.id) return;
    
    try {
      // Fetch counts for Credit (1) and Purchase (2) types
      // await dispatch(fetchQuotesByUserIdWithFilters({
      //   userId: user.id,
      //   filters: {
      //     purchaseTypeIds: "1,2"
      //   },
      //   sort: [{ orderBy: "createdAt", order: "desc" }],
      //   page: 1,
      //   limit: 1 // Only need count
      // }));
    } catch (error) {
      console.error("Error fetching purchased quotes count:", error);
    }
  };

  // Load counts and offers on component mount and when tab changes
  useEffect(() => {
    if (user?.id) {
      if (activeTab === "purchased") {
        fetchPurchasedQuotesCount();
      } else {
        // First fetch offers to populate the list, then fetch counts
        // This prevents counts from overwriting the main offers list
        const fetchData = async () => {
          await fetchOffers();
          await fetchAllCounts(true, activeTab);
        };
        fetchData();
      }
    }
  }, [user?.id, activeTab]);

  // Fetch offers based on filters (but not when tab changes, that's handled above)
  useEffect(() => {
    if (activeTab !== "purchased" && user?.id && countsInitialized) {
      fetchOffers();
    }
  }, [currentPage, searchQuery, statusFilter, sortBy, dateRange]);

  // Helper function to convert status filter to status ID
  const getStatusId = (status: string) => {
    const statusMap: { [key: string]: number } = {
      approved: 2,
      accepted: 3,
      rejected: 4,
      expired: 5,
      cancelled: 6,
      completed: 7,
      archived: 8,
      contracted: 15
    };
    return statusMap[status];
  };

  // Helper function to convert status filter to status name
  const getStatusName = (status: string) => {
    const statusMap: { [key: string]: string } = {
      approved: "Approved",
      accepted: "Accepted",
      rejected: "Rejected",
      expired: "Expired",
      cancelled: "Cancelled",
      completed: "Completed",
      archived: "Archived",
      contracted: "Contracted"
    };
    return statusMap[status];
  };

  // Check if user needs to complete profile
  const checkProfileCompletion = () => {
    if (user && !user.phone) {
      setShowProfileCompletionDialog(true);
      return false; // Profile incomplete
    }
    return true; // Profile complete
  };

  // Track when verification data has loaded
  useEffect(() => {
    if (verificationData !== null && verificationData !== undefined) {
      setVerificationDataLoaded(true);
    }
  }, [verificationData]);

  // Check if user needs verification
  const checkVerification = () => {
    // Don't check verification if data hasn't loaded yet
    if (!verificationDataLoaded) {
      return true; // Allow action to proceed while loading
    }
    
    if (!isVerified()) {
      setShowVerificationDialog(true);
      return false; // Verification incomplete
    }
    return true; // Verification complete
  };

  // Check both profile and verification
  const checkRequirements = () => {
    // Don't block actions if verification data is still loading
    if (!verificationDataLoaded) {
      // Only check profile completion while verification loads
      if (!checkProfileCompletion()) {
        return false;
      }
      return true;
    }
    
    // Check verification first (higher priority)
    if (!checkVerification()) {
      return false;
    }
    
    // Then check profile completion
    if (!checkProfileCompletion()) {
      return false;
    }
    
    return true;
  };

  // Navigation function for profile completion
  const navigateToProfile = () => {
    if (user?.role?.id) {
      const roleId = Number(user.role.id);
      if (roleId === 1) { // Professional
        navigate("/professional/profile");
      } else if (roleId === 2) { // International Professional
        navigate("/international-professional/profile");
      } else if (roleId === 3) { // Client
        navigate("/client/profile");
      } else if (roleId === 4) { // Admin
        navigate("/admin/profile");
      }
    }
  };

  // Handle billing saved callback
  const handleBillingSaved = () => {
    // Check if user now has billing information
    const billingCheck = checkUserBillingInfo(user);
    
    if (billingCheck.hasBillingInfo && selectedOfferForBilling) {
      // Proceed with accepting the offer
      handleUpdateOffer(selectedOfferForBilling, OfferStatusEnum.Accepted);
      setSelectedOfferForBilling(null);
    }
  };

  // Helper function to convert sort option to API parameters
  const getSortParams = (sort: string) => {
    switch (sort) {
      case "newest":
        return [{ orderBy: "createdAt", order: "DESC" }];
      case "oldest":
        return [{ orderBy: "createdAt", order: "ASC" }];
      case "highest":
        return [{ orderBy: "amount", order: "DESC" }];
      case "lowest":
        return [{ orderBy: "amount", order: "ASC" }];
      default:
        return [{ orderBy: "createdAt", order: "DESC" }];
    }
  };

  // Helper function to get date range filter
  const getDateRangeFilter = (range: string) => {
    const now = new Date();
    let startDate: Date | undefined;

    switch (range) {
      case "today":
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case "week":
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "all":
      default:
        return undefined;
    }

    return startDate ? startDate.toISOString() : undefined;
  };

  const fetchOffers = async () => {
    if (!user?.id) return;
    
    setIsLoadingCurrentTab(true);
    try {
      // API Logic: isSent=true means offers sent TO the user (received), isSent=false means offers sent BY the user (sent)
      const isSentParam = activeTab === "received"; 

      const params: any = {
        page: currentPage,
        limit: pageSize,
        isSent: isSentParam,
        userId: user?.id,
        search: searchQuery || undefined,
        statusIds: [OfferStatusEnum.Approved, OfferStatusEnum.Accepted, OfferStatusEnum.Rejected, OfferStatusEnum.Cancelled, OfferStatusEnum.Deleted, OfferStatusEnum.Admin_Suspended, OfferStatusEnum.Admin_Rejected, OfferStatusEnum.Admin_Deleted].toString(),
        status: statusFilter !== "all" ? getStatusId(statusFilter) : undefined,
        sort: getSortParams(sortBy)
      };

      // Add date range filter if applicable
      const dateFilter = getDateRangeFilter(dateRange);
      if (dateFilter) {
        params.fromDate = dateFilter;
      }

      await dispatch(fetchUserOffers(params)).unwrap();
      
    } catch (error) {
      console.error("Error fetching offers:", error);
    } finally {
      setIsLoadingCurrentTab(false);
    }
  };

  // Use offers directly from Redux - API already filters by tab
  // No need for client-side filtering since API returns filtered data based on isSent parameter
  const currentTabOffers = offers || [];


  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle sort change
  const handleSortChange = (sort: string) => {
    setSortBy(sort);
    setCurrentPage(1); // Reset to first page when sort changes
  };

  // Handle date range change
  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    setCurrentPage(1); // Reset to first page when date range changes
  };

  // Handle tab change
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    // Save the selected tab to localStorage
    localStorage.setItem("myOffersActiveTab", tab);
    setCurrentPage(1); // Reset to first page when tab changes
    setSearchQuery(""); // Reset search when tab changes
    setStatusFilter("all"); // Reset status filter when tab changes
    setSortBy("newest"); // Reset sort when tab changes
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle offer acceptance (for inline actions)
  const handleUpdateOffer = async (offer: any, status: number) => {
    // Check profile and verification requirements first
    if (!checkRequirements()) {
      return; // Stop execution if requirements not met
    }

    // Check if user has billing information for accepting offers
    const billingCheck = checkUserBillingInfo(user);
    
    if (!billingCheck.hasBillingInfo && status === OfferStatusEnum.Accepted) {
      // Store the offer for auto-accepting after billing setup
      setSelectedOfferForBilling(offer);
      // Show billing address requirement dialog
      setShowBillingAddressDialog(true);
      return;
    }

    try {
      // Update offer status to accepted (status ID: 3)
      await dispatch(updateOffer({
        id: offer.id,
        data: {
          status: {
            id: status
          },
          timelineType: { id: Number(offer.timelineType.id) },
          files: offer.files ? offer.files : [],
          user: { id: offer.user.id },
          job: { id: offer?.job?.id },
        }
      })).unwrap();
      setSelectedOffer(null);
      // Refresh offers and counts after update
      // Fetch offers first, then counts to prevent overwriting
      await fetchOffers();
      await fetchAllCounts(true, activeTab);
    } catch (error) {
      console.error("Error updating offer:", error);
    }
  };

  // Handle offer deletion with confirmation (for inline actions)
  const handleDeleteOffer = async (offer: any) => {
    const isConfirmed = window.confirm(t("myOffersPageProfessional.deleteConfirmation"));

    if (!isConfirmed) return;

    try {
      // Update offer status to deleted (status ID: 8)
      await dispatch(updateOffer({
        id: offer.id,
        data: {
          status: {
            id: 8
          },
          timelineType: { id: Number(offer.timelineType.id) },
          files: offer.files ? offer.files : [],
          user: { id: offer.user.id },
          job: { id: offer?.job?.id },
        }
      })).unwrap();

      // Refresh offers and counts after update
      // Fetch offers first, then counts to prevent overwriting
      await fetchOffers();
      await fetchAllCounts(true, activeTab);
    } catch (error) {
      console.error("Error deleting offer:", error);
    }
  };

  // Handle offer permanent deletion with confirmation (for inline actions)
  const handlePermanentlyDeleteOffer = async (offer: any) => {
    const isConfirmed = window.confirm(t("myOffersPageProfessional.permanentDeleteConfirmation"));

    if (!isConfirmed) return;

    try {
      // Update offer status to permanently deleted (status ID: 10)
      await dispatch(updateOffer({
        id: offer.id,
        data: {
          status: {
            id: 10
          },
          timelineType: { id: Number(offer.timelineType.id) },
          files: offer.files ? offer.files : [],
          user: { id: offer.user.id },
          job: { id: offer?.job?.id },
        }
      })).unwrap();

      // Refresh offers and counts after update
      // Fetch offers first, then counts to prevent overwriting
      await fetchOffers();
      await fetchAllCounts(true, activeTab);
    } catch (error) {
      console.error("Error permanently deleting offer:", error);
    }
  };


  // Status badge component
  const StatusBadge = ({ status }) => {
    // Handle both string and object status formats
    const getStatusInfo = (status) => {
      if (typeof status === "object" && status !== null && status.id !== undefined) {
        const statusId = status.id;
        const statusMap = {
          1: { text: t("myOffersPageProfessional.draft"), style: "bg-gray-100 text-gray-800" },
          2: { text: t("myOffersPageProfessional.approved"), style: "bg-yellow-100 text-yellow-800" },
          3: { text: t("myOffersPageProfessional.accepted"), style: "bg-green-100 text-green-800" },
          4: { text: t("myOffersPageProfessional.rejected"), style: "bg-red-100 text-red-800" },
          5: { text: t("myOffersPageProfessional.expired"), style: "bg-gray-100 text-gray-800" },
          6: { text: t("myOffersPageProfessional.suspended"), style: "bg-orange-100 text-orange-800" },
          7: { text: t("myOffersPageProfessional.completed"), style: "bg-blue-100 text-blue-800" },
          8: { text: t("myOffersPageProfessional.archived"), style: "bg-purple-100 text-purple-800" },
          9: { text: t("myOffersPageProfessional.deletedCustomer"), style: "bg-red-100 text-red-800" },
          10: { text: t("myOffersPageProfessional.permanentlyDeleted"), style: "bg-red-900 text-white" }
        };
        return statusMap[statusId] || { text: t("myOffersPageProfessional.unknown"), style: "bg-gray-100 text-gray-800" };
      } else {
        // Handle string status (for purchased quotes)
        const statusStyles = {
          active: "bg-purple-100 text-purple-800",
          responded: "bg-teal-100 text-teal-800"
        };
        const statusText = {
          active: t("myOffersPageProfessional.active"),
          responded: t("myOffersPageProfessional.responded")
        };
        return {
          text: statusText[status] || status || t("myOffersPageProfessional.unknown"),
          style: statusStyles[status] || "bg-gray-100 text-gray-800"
        };
      }
    };

    const statusInfo = getStatusInfo(status);

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.style}`}>
        {statusInfo.text}
      </span>
    );
  };


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <SharedHeader4 />

      <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t("myOffersPageProfessional.title")}</h1>
          <p className="text-gray-600 mt-1">{t("myOffersPageProfessional.subtitle")}</p>
        </div>

        {/* Offers Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">{activeTab === "purchased" ? t("myOffersPageProfessional.totalQuotes") : t("myOffersPageProfessional.totalOffers")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {countsLoading ? "..." : (activeTab === "purchased" ? purchasedQuotesCount : stats.totalOffers)}
              </h3>
              <p className="text-sm text-gray-600">
                {activeTab === "received" ? t("myOffersPageProfessional.receivedOffers") : activeTab === "sent" ? t("myOffersPageProfessional.sentOffers") : t("myOffersPageProfessional.purchasedQuotes")}
              </p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">{activeTab === "purchased" ? t("myOffersPageProfessional.used") : t("myOffersPageProfessional.accepted")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {activeTab === "purchased" ? 0 : (countsLoading ? "..." : stats.acceptedOffers)}
              </h3>
              <p className="text-sm text-gray-600">{activeTab === "purchased" ? t("myOffersPageProfessional.usedQuotes") : t("myOffersPageProfessional.acceptedOffers")}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-500">{activeTab === "purchased" ? t("myOffersPageProfessional.active") : t("myOffersPageProfessional.pending")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {activeTab === "purchased" ? 0 : (countsLoading ? "..." : stats.pendingOffers)}
              </h3>
              <p className="text-sm text-gray-600">{activeTab === "purchased" ? t("myOffersPageProfessional.activeQuotes") : t("myOffersPageProfessional.awaitingResponse")}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <span className="text-sm text-gray-500">{activeTab === "purchased" ? t("myOffersPageProfessional.inactive") : t("myOffersPageProfessional.rejected")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {activeTab === "purchased" ? 0 : (countsLoading ? "..." : stats.rejectedOffers)}
              </h3>
              <p className="text-sm text-gray-600">{activeTab === "purchased" ? t("myOffersPageProfessional.inactiveQuotes") : t("myOffersPageProfessional.rejectedOffers")}</p>
            </div>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="mb-8">
          <div className="p-4 border-b">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 sm:flex-none sm:min-w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("myOffersPageProfessional.searchOffers")}
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  <Filter className="h-5 w-5" />
                  <span>{t("myOffersPageProfessional.filters")}</span>
                </button>
              </div>

              <div className="flex items-center gap-4">
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="newest">{t("myOffersPageProfessional.newestFirst")}</option>
                  <option value="oldest">{t("myOffersPageProfessional.oldestFirst")}</option>
                  <option value="highest">{t("myOffersPageProfessional.highestAmount")}</option>
                  <option value="lowest">{t("myOffersPageProfessional.lowestAmount")}</option>
                </select>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-4 pt-4 border-t"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("myOffersPageProfessional.dateRange")}
                      </label>
                      <select
                        value={dateRange}
                        onChange={(e) => handleDateRangeChange(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">{t("myOffersPageProfessional.allTime")}</option>
                        <option value="today">{t("myOffersPageProfessional.today")}</option>
                        <option value="week">{t("myOffersPageProfessional.thisWeek")}</option>
                        <option value="month">{t("myOffersPageProfessional.thisMonth")}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("myOffersPageProfessional.status")}
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => handleStatusFilterChange(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">{t("myOffersPageProfessional.allStatus")}</option>
                        <option value="approved">{t("myOffersPageProfessional.approved")}</option>
                        <option value="accepted">{t("myOffersPageProfessional.accepted")}</option>
                        <option value="rejected">{t("myOffersPageProfessional.rejected")}</option>
                        <option value="expired">{t("myOffersPageProfessional.expired")}</option>
                        <option value="completed">{t("myOffersPageProfessional.completed")}</option>
                        <option value="cancelled">{t("myOffersPageProfessional.cancelled")}</option>
                        <option value="archived">{t("myOffersPageProfessional.archived")}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("myOffersPageProfessional.offerType")}
                      </label>
                      <select
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">{t("myOffersPageProfessional.allTypes")}</option>
                        <option value="sent">{t("myOffersPageProfessional.sent")}</option>
                        <option value="received">{t("myOffersPageProfessional.received")}</option>
                        <option value="purchased">{t("myOffersPageProfessional.purchased")}</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => {
                        setDateRange("all");
                        setStatusFilter("all");
                        setShowFilters(false);
                      }}
                      className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      {t("myOffersPageProfessional.resetFilters")}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Offer Type Tabs */}
          <div className="px-4 border-b">
            <div className="flex space-x-6 overflow-x-auto">
              <button
                onClick={() => handleTabChange("received")}
                className={`py-4 px-2 relative ${activeTab === "received"
                  ? "text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                {t("myOffersPageProfessional.receivedOffersTab")}
                {activeTab === "received" && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                  />
                )}
              </button>
              <button
                onClick={() => handleTabChange("sent")}
                className={`py-4 px-2 relative ${activeTab === "sent"
                  ? "text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                {t("myOffersPageProfessional.sentOffersTab")}
                {activeTab === "sent" && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                  />
                )}
              </button>
              <button
                onClick={() => handleTabChange("purchased")}
                className={`py-4 px-2 relative ${activeTab === "purchased"
                  ? "text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                {t("myOffersPageProfessional.purchasedQuotesTab")}
                {activeTab === "purchased" && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                  />
                )}
              </button>
            </div>
          </div>

          {/* Offers List */}
          {activeTab === "purchased" ? (
            // Show Credit (1), Purchase (2), and Promotional (4) type quotes
            <PurchasedQuoteListing userId={user?.id} purchaseTypeIds="1,2,4" />
          ) : (
            <div className="divide-y">
              {isLoadingCurrentTab ? (
                <div className="p-6 text-center">
                  <div className="flex items-center justify-center">
                    <Clock className="h-5 w-5 animate-spin mr-2" />
                    {t("myOffersPageProfessional.loadingOffers", { tab: activeTab })}
                  </div>
                </div>
              ) : currentTabOffers?.length > 0 ? (
                currentTabOffers.map((offer: any) => (
                  <div
                    key={offer.id}
                    className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex flex-col sm:flex-row justify-between gap-4" onClick={() => setSelectedOffer(offer)}>
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-medium text-gray-900">
                            {activeTab === "purchased" ? offer.projectTitle : (offer.title || offer.job?.title || t("myOffersPageProfessional.untitledJob"))}
                          </h3>
                          {activeTab === "purchased" && offer.isPriority && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                              {t("myOffersPageProfessional.priority")}
                            </span>
                          )}
                          {(offer.job?.isEmergency || offer.isEmergency) && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {t("myOffersPageProfessional.emergencyExitFee", { 
                                amount: `${getDynamicCurrencySymbol()}${calculateEmergencyCost(
                                  offer.job?.emergencyTimeSlot || offer.emergencyTimeSlot,
                                  offer.job?.emergencyDays || offer.emergencyDays,
                                  categoryFormSettings
                                )}`
                              })}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {activeTab === "purchased" ? t("myOffersPageProfessional.quoteId", { id: offer.id }) : t("myOffersPageProfessional.offerId", { id: offer.id })} • {
                            activeTab === "purchased"
                              ? offer.client.location
                              : (offer.job?.userId?.location || offer.job?.userId?.address || t("myOffersPageProfessional.locationNotSpecified"))
                          }
                        </p>
                        {/* Client Information - Received tab */}
                        {offer?.job?.userId && (
                          <div className="flex flex-col md:flex-row md:items-center gap-4 mt-2">
                            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                              {(() => {
                                const c = offer.job.userId;
                                const photoUrl = c?.photo?.path || c?.photo?.url || c?.profilePhoto || "";
                                const name = (c?.firstName && c?.lastName) ? `${c.firstName} ${c.lastName}` : (c?.firstName || c?.name || "Client");
                                return photoUrl ? (
                                  <img src={photoUrl} alt={name} className="h-12 w-12 rounded-full object-cover" />
                                ) : (
                                  <User className="h-6 w-6 text-gray-600" />
                                );
                              })()}
                            </div>
                            <div className="space-y-1 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">
                                  {(offer.job.userId?.firstName && offer.job.userId?.lastName)
                                    ? `${offer.job.userId.firstName} ${offer.job.userId.lastName}`
                                    : (offer.job.userId?.firstName || offer.job.userId?.name || "Client")}
                                </span>
                                {offer.job.userId?.verified && (
                                  <Badge variant={undefined} className="bg-green-50 text-green-700">{t("myOffersPageProfessional.verified")}</Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                  <span>{offer.job.userId?.averageRating || 0}</span>
                                </div>
                                {offer.job.userId?.reviewsCount && (
                                  <span>{t("myOffersPageProfessional.reviewsCount", { count: offer.job.userId.reviewsCount })}</span>
                                )}
                                <span className="hidden md:inline">•</span>
                                {offer.job.userId?.completedJobs && (
                                  <span>{t("myOffersPageProfessional.jobsCompleted", { count: offer.job.userId.completedJobs })}</span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                {offer.job.userId?.avgResponseTime && (
                                  <div className="flex items-center gap-1">
                                    <Timer className="h-4 w-4" />
                                    <span>{t("myOffersPageProfessional.responseTime", { time: offer.job.userId.avgResponseTime })}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  <span>{offer.job.userId?.location || offer.job.userId?.address || t("myOffersPageProfessional.locationNotSpecified")}</span>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedClientUser(offer.job.userId);
                                setShowClientDialog(true);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:underline self-start md:self-center"
                            >
                              {t("myOffersPageProfessional.viewProfile")}
                            </button>
                          </div>
                        )}
                        <p className="text-sm text-gray-600 mt-2">
                          {activeTab === "purchased" ? offer.description : (offer.description || offer.job?.description || t("myOffersPageProfessional.noDescriptionAvailable"))}
                        </p>
                      </div>
                      <div className="flex flex-col sm:items-end gap-2">
                        <StatusBadge status={offer.status} />
                        <p className="text-lg font-medium text-gray-900">
                          {activeTab === "purchased"
                            ? `${offer.purchaseCurrency === "USD" ? getDynamicCurrencySymbol() : "💎"}${offer.purchaseAmount.toFixed(2)}`
                            : `${getDynamicCurrencySymbol()}${offer.amount || offer.job?.budget || 0}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          {activeTab === "purchased"
                            ? t("myOffersPageProfessional.purchasedDate", { date: new Date(offer.purchaseDate).toLocaleDateString() })
                            : t("myOffersPageProfessional.createdDate", { date: new Date(offer.createdAt || offer.createdDate).toLocaleDateString() })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-4">
                        <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" onClick={(e) => {
                          e.stopPropagation();
                          if (offer.user?.id) {
                            window.location.href = "/client/MessageAndVideoCall?professionalId=" + offer.user.id + "&professionalName=" + encodeURIComponent(offer.user.firstName || offer.user.name || "Professional");
                            // navigate(`/client/MessageAndVideoCall?professionalId=${offer.user.id}&professionalName=${encodeURIComponent(offer.user.firstName || offer.user.name || "Professional")}`);
                          }
                        }}>
                          <MessageCircle className="h-4 w-4" />
                          {t("myOffersPageProfessional.messages", { count: activeTab === "purchased" ? (offer.messages || 0) : 0 })}
                        </button>
                        <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                          <FileText className="h-4 w-4" />
                          {t("myOffersPageProfessional.files", { count: activeTab === "purchased" ? (offer.attachments || 0) : (offer.files?.length || 0) })}
                        </button>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          {activeTab === "purchased"
                            ? (offer.client?.rating || 0)
                            : (offer.user?.rating || 0)
                          }
                        </div>
                      </div>
                      
                      {/* See More Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOffer(offer);
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {t("myOffersPageProfessional.seeMore")}
                      </button>
                    </div>

                    {activeTab === "received" && offer.status?.id && Number(offer.status.id) !== OfferStatusEnum.Deleted && Number(offer.status.id) !== OfferStatusEnum.Admin_Suspended && Number(offer.status.id) !== OfferStatusEnum.Admin_Deleted && (
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2 mt-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOffer(offer);
                          }}
                          className="px-3 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 text-sm sm:text-base flex-1 sm:flex-none flex items-center justify-center gap-2"
                        >
                          <ExternalLink className="h-4 w-4" />
                          {t("myOffersPageProfessional.seeMore")}
                        </button>
                        
                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="flex flex-col sm:flex-row gap-2">
                            {offer.status?.id && Number(offer.status.id) !== OfferStatusEnum.Rejected && <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateOffer(offer, OfferStatusEnum.Rejected);
                              }}
                              className="px-3 py-2 border rounded-lg hover:bg-gray-50 text-red-600 text-sm sm:text-base flex-1 sm:flex-none"
                            >
                              {t("myOffersPageProfessional.decline")}
                            </button>}
                            {offer.status?.id && Number(offer.status.id) !== OfferStatusEnum.Accepted && <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateOffer(offer, OfferStatusEnum.Accepted);
                              }}
                              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base flex-1 sm:flex-none"
                            >
                              {t("myOffersPageProfessional.accept")}
                            </button>}
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteOffer(offer);
                              }}
                              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm sm:text-base flex-1 sm:flex-none"
                            >
                              {t("myOffersPageProfessional.delete")}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (offer.user?.id) {
                                  window.location.href = "/client/MessageAndVideoCall?professionalId=" + offer.user.id + "&professionalName=" + encodeURIComponent(offer.user.firstName || offer.user.name || "Professional");
                                  // navigate(`/client/MessageAndVideoCall?professionalId=${offer.user.id}&professionalName=${encodeURIComponent(offer.user.firstName || offer.user.name || "Professional")}`);
                                }
                              }}
                              className="px-3 py-2 border rounded-lg hover:bg-gray-50 text-blue-600 text-sm sm:text-base flex-1 sm:flex-none"
                            >
                              {t("myOffersPageProfessional.chat")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {offer.status?.id && (Number(offer.status.id) === OfferStatusEnum.Deleted || Number(offer.status.id) === OfferStatusEnum.Archived || Number(offer.status.id) === OfferStatusEnum.Admin_Deleted) && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4">
                        <p className="text-sm text-gray-600">
                          {offer.status?.id && Number(offer.status.id) === OfferStatusEnum.Admin_Deleted ? t("myOffersPageProfessional.deletedByAdmin") : t("myOffersPageProfessional.customerDeletedMessage")}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePermanentlyDeleteOffer(offer);
                          }}
                          className="px-3 py-2 bg-red-900 text-white rounded-lg hover:bg-red-950 text-sm sm:text-base"
                        >
                          {t("myOffersPageProfessional.permanentlyDelete")}
                        </button>
                      </div>
                    )}
                    {activeTab === "sent" && offer.status?.id && Number(offer.status.id) !== OfferStatusEnum.Deleted && (
                      <div className="mt-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOffer(offer);
                            }}
                            className="w-full sm:w-auto px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            <ExternalLink className="h-4 w-4" />
                            {t("myOffersPageProfessional.seeMore")}
                          </button>
                          
                          <div className="flex flex-col sm:flex-row gap-3">
                            {offer.status?.id && Number(offer.status.id) != OfferStatusEnum.Admin_Suspended && Number(offer.status.id) !== OfferStatusEnum.Admin_Deleted && (
                              <><button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteOffer(offer);
                                }}
                                className="w-full sm:w-auto px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors"
                              >
                                {t("myOffersPageProfessional.delete")}
                              </button>                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (offer.job?.userId?.id) {
                                    window.location.href = "/client/MessageAndVideoCall?professionalId=" + offer.job.userId.id + "&professionalName=" + encodeURIComponent(offer.job.userId.firstName || offer.job.userId.name || "Client");
                                    // navigate(`/client/MessageAndVideoCall?professionalId=${offer.job.userId.id}&professionalName=${encodeURIComponent(offer.job.userId.firstName || offer.job.userId.name || "Client")}`);
                                  }
                                }}
                                className="w-full sm:w-auto px-4 py-3 border-2 border-blue-600 rounded-lg hover:bg-blue-50 text-blue-600 text-sm font-medium transition-colors"
                              >
                                {t("myOffersPageProfessional.chat")}
                              </button></>
                            )}
                            {offer.status?.id && Number(offer.status.id) !== OfferStatusEnum.Admin_Deleted && <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingOffer(offer);
                                setShowEditModal(true);
                              }}
                              className="w-full sm:w-auto px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                            >
                              {t("myOffersPageProfessional.edit")}
                            </button>}
                          </div>
                        </div>
                      </div>
                    )}
                    {offer.type === "purchased" && (
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOffer(offer);
                          }}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium transition-colors flex items-center gap-2"
                        >
                          <ExternalLink className="h-4 w-4" />
                          {t("myOffersPageProfessional.seeMore")}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle view quote details
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          {t("myOffersPageProfessional.viewQuoteDetails")}
                        </button>
                      </div>
                    )}
                  </div>
                ))
            ) : (
              <div className="p-6 text-center text-gray-500">
                {t("myOffersPageProfessional.noOffersFound")}
              </div>
            )}
            </div>
          )}

          {/* Pagination */}
          {activeTab !== "purchased" && currentTabOffers.length > 0 && (totalCount > pageSize || hasNextPage) && (
            <div className="px-4 py-4 border-t flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  {t("myOffersPageProfessional.showingPage", { current: currentPage, total: Math.ceil(totalCount / pageSize), count: totalCount || 0 })}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                {/* Previous/Next Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("myOffersPageProfessional.previous")}
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage && currentPage >= (totalPages || 1)}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("myOffersPageProfessional.next")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Client Profile Dialog */}
        <AnimatePresence>
          {showClientDialog && selectedClientUser && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-50"
                onClick={() => setShowClientDialog(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="fixed inset-4 z-50 overflow-hidden"
              >
                <div className="h-full bg-white rounded-lg shadow-xl flex flex-col">
                  <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t("myOffersPageProfessional.clientProfile", { 
                        name: (selectedClientUser?.firstName && selectedClientUser?.lastName)
                          ? `${selectedClientUser.firstName} ${selectedClientUser.lastName}`
                          : (selectedClientUser?.firstName || selectedClientUser?.name || "Client")
                      })}
                    </h2>
                    <button
                      onClick={() => setShowClientDialog(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4">
                    <ClientProfile1
                      isAdminEdit={true}
                      adminUserId={selectedClientUser.id}
                      canEdit={false}
                    />
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Offer Details Modal */}
        <AnimatePresence>
          {selectedOffer && (
            <OfferDetailsModal
              offer={selectedOffer}
              onClose={() => setSelectedOffer(null)}
              activeTab={activeTab}
              onRefresh={fetchOffers}
              categoryFormSettings={categoryFormSettings}
            />
          )}
        </AnimatePresence>

        {/* Edit Offer Modal */}
        {showEditModal && editingOffer && (
          <RequestQuoteModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingOffer(null);
            }}
            professional={professionalForEdit}
            isProfessional={true}
            selectedJobId={editingOffer?.job?.id}
            isSentDefault={false}
            offerId={editingOffer.id}
            existingOffer={editingOffer}
            onOfferUpdated={(updatedOffer) => {
              // Refresh offers after update
              fetchOffers();
              setShowEditModal(false);
              setEditingOffer(null);
            }}
          />
        )}
      </main>

      {/* Footer Actions - Mobile Only */}
      {!isLoadingCurrentTab && <SharedFooter3 />}

      {/* Profile Completion Dialog */}
      <ProfileCompletionDialog
        isOpen={showProfileCompletionDialog}
        onClose={() => setShowProfileCompletionDialog(false)}
        onGoToProfile={navigateToProfile}
        userRole={Number(user?.role?.id)}
      />

      {/* Verification Requirement Dialog */}
      <VerificationRequirementDialog
        isOpen={showVerificationDialog}
        onClose={() => setShowVerificationDialog(false)}
        title={t("myOffersPageProfessional.verificationRequiredTitle")}
        message={t("myOffersPageProfessional.verificationRequiredMessage")}
      />

      {/* Billing Address Requirement Dialog */}
      <BillingAddressRequirementDialog
        isOpen={showBillingAddressDialog}
        onClose={() => {
          setShowBillingAddressDialog(false);
          setSelectedOfferForBilling(null);
        }}
        onBillingSaved={handleBillingSaved}
        userRole={Number(user?.role?.id)}
        action="payment"
      />
    </div>
  );
};

export default OffersDashboard;